import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismComboSelectInput } from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import GeneralDropdownsTriggersTriggerPowerSelectKebab from 'lumen/components/general/dropdowns/triggers/trigger-power-select-kebab';

const ACTION_TYPE = {
  VIEW_PROPERTY_ACCESS: 'viewPropertyAccess',
  REMOVE_USER: 'removeUser',
} as const;

type ActionOption = Option & {
  action: (typeof ACTION_TYPE)[keyof typeof ACTION_TYPE];
};

interface CellActionsSignature {
  Args: {
    cellData: string;
    row: {
      hideRemoveUser?: boolean;
      isDisabled?: boolean;
      isLocked?: boolean;
      onDelete?: () => void;
      onViewPropertyAccess?: () => void;
    };
  };
}

export default class CellActions extends Component<CellActionsSignature> {
  @service declare intl: Services['intl'];

  get dropdownOptions(): ActionOption[] {
    const options: ActionOption[] = [
      {
        action: ACTION_TYPE.VIEW_PROPERTY_ACCESS,
        key: ACTION_TYPE.VIEW_PROPERTY_ACCESS,
        label: this.intl.t('settings.users.action.view_property_access'),
      },
    ];

    if (!this.args.row.hideRemoveUser) {
      options.push({
        action: ACTION_TYPE.REMOVE_USER,
        key: ACTION_TYPE.REMOVE_USER,
        label: this.intl.t('settings.users.action.remove_user_from_property'),
        disabled: this.args.row.isLocked,
        disabledMessage: this.args.row.isLocked
          ? this.intl.t(
              'settings.users.action.remove_user_from_property_disabled',
            )
          : undefined,
      });
    }

    return options;
  }

  @action
  onSelect(option: ActionOption): void {
    switch (option.action) {
      case ACTION_TYPE.VIEW_PROPERTY_ACCESS:
        this.args.row.onViewPropertyAccess?.();
        break;
      case ACTION_TYPE.REMOVE_USER:
        this.args.row.onDelete?.();
        break;
    }
  }

  <template>
    <PrismComboSelectInput
      @disabled={{@row.isDisabled}}
      @horizontalPosition="right"
      @isSecondary={{true}}
      @onChange={{this.onSelect}}
      @options={{this.dropdownOptions}}
      @unsafe_triggerComponent={{component
        GeneralDropdownsTriggersTriggerPowerSelectKebab
        buttonType="secondary"
        size="small"
      }}
      data-test-id="cell-actions-options"
    />
  </template>
}
